/**
 * Rule configuration and coverage validation (E19-5: MED-501)
 * Updated for dynamic tier-based escalation (MED-543)
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Stack,
  Group,
  Button,
  Badge,
  ActionIcon,
  Text,
  Modal,
  Select,
  Switch,
  NumberInput,
  Loader,
  Center,
  Paper,
  Alert,
  Divider,
  Title,
  Box,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { Plus, Pencil, Trash2, Check, AlertTriangle, Info } from 'lucide-react';
import {
  absenceEmailRulesApi,
  absenceEmailTemplatesApi,
  type AbsenceEmailRuleWithTemplateName,
  type AbsenceEmailTemplate,
  type CreateRuleInput,
  type CoverageAnalysis,
  type AbsenceRuleType,
  type EscalationTier,
} from '../../../../services/api/absenceEmailManagement';

const ABSENCE_TYPE_OPTIONS = [
  { value: 'unexcused', label: 'Unexcused' },
  { value: 'business', label: 'Business' },
  { value: 'personal', label: 'Personal' },
  { value: 'any', label: 'Any' },
];

const ESCALATION_TIER_OPTIONS = [
  { value: 'first_warning', label: 'First Warning' },
  { value: 'first_warning_with_offer', label: 'First Warning + Offer' },
  { value: 'mid_escalation', label: 'Mid-Escalation' },
  { value: 'deferral_offer', label: 'Deferral Offer' },
  { value: 'forced_deferral', label: 'Forced Deferral' },
];

const TIER_COLORS: Record<string, string> = {
  first_warning: 'blue',
  first_warning_with_offer: 'cyan',
  mid_escalation: 'warningAmber',
  deferral_offer: 'orange',
  forced_deferral: 'red',
};

interface RuleFormData {
  escalation_tier: EscalationTier;
  absence_type: AbsenceRuleType;
  is_first_session: boolean;
  no_email: boolean;
  template_id: string | null;
  priority: number;
}

const defaultRuleForm: RuleFormData = {
  escalation_tier: 'first_warning',
  absence_type: 'unexcused',
  is_first_session: false,
  no_email: false,
  template_id: null,
  priority: 0,
};

export function AbsenceRuleConfig() {
  const [rules, setRules] = useState<AbsenceEmailRuleWithTemplateName[]>([]);
  const [templates, setTemplates] = useState<AbsenceEmailTemplate[]>([]);
  const [coverage, setCoverage] = useState<CoverageAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] =
    useDisclosure(false);
  const [editingRule, setEditingRule] = useState<AbsenceEmailRuleWithTemplateName | null>(null);
  const [deletingRule, setDeletingRule] = useState<AbsenceEmailRuleWithTemplateName | null>(null);
  const [formData, setFormData] = useState<RuleFormData>(defaultRuleForm);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [rulesData, templatesData, coverageData] = await Promise.all([
        absenceEmailRulesApi.list(),
        absenceEmailTemplatesApi.list(),
        absenceEmailRulesApi.getCoverage(),
      ]);
      setRules(rulesData || []);
      setTemplates((templatesData || []).filter((t) => t.is_active));
      setCoverage(coverageData);
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to load rules', color: 'red' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenCreate = (tier: EscalationTier) => {
    setEditingRule(null);
    setFormData({ ...defaultRuleForm, escalation_tier: tier });
    openModal();
  };

  const handleOpenEdit = (rule: AbsenceEmailRuleWithTemplateName) => {
    setEditingRule(rule);
    setFormData({
      escalation_tier: rule.escalation_tier,
      absence_type: rule.absence_type,
      is_first_session: rule.is_first_session,
      no_email: rule.no_email,
      template_id: rule.template_id,
      priority: rule.priority,
    });
    openModal();
  };

  const handleOpenDelete = (rule: AbsenceEmailRuleWithTemplateName) => {
    setDeletingRule(rule);
    openDeleteModal();
  };

  const handleSave = async () => {
    if (!formData.no_email && !formData.template_id) {
      notifications.show({
        title: 'Validation Error',
        message: 'Select a template or mark as "No Email"',
        color: 'red',
      });
      return;
    }

    setSaving(true);
    try {
      const payload: CreateRuleInput = {
        escalation_tier: formData.escalation_tier,
        absence_type: formData.absence_type,
        is_first_session: formData.is_first_session,
        no_email: formData.no_email,
        template_id: formData.no_email ? null : formData.template_id,
        priority: formData.priority,
      };

      if (editingRule) {
        await absenceEmailRulesApi.update(editingRule.id, payload);
        notifications.show({ title: 'Success', message: 'Rule updated', color: 'green' });
      } else {
        await absenceEmailRulesApi.create(payload);
        notifications.show({ title: 'Success', message: 'Rule created', color: 'green' });
      }
      closeModal();
      fetchData();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to save rule';
      notifications.show({ title: 'Error', message, color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingRule) return;
    try {
      await absenceEmailRulesApi.delete(deletingRule.id);
      notifications.show({ title: 'Success', message: 'Rule deleted', color: 'green' });
      closeDeleteModal();
      fetchData();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to delete rule';
      notifications.show({ title: 'Error', message, color: 'red' });
    }
  };

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  const templateOptions = templates.map((t) => ({ value: t.id, label: t.name }));

  // Group rules by tier
  const tierGroups = ESCALATION_TIER_OPTIONS.map((opt) => ({
    tier: opt.value as EscalationTier,
    label: opt.label,
    rules: rules.filter((r) => r.escalation_tier === opt.value),
  }));

  const tierLabel = (tier: string) =>
    ESCALATION_TIER_OPTIONS.find((o) => o.value === tier)?.label ?? tier;

  return (
    <Stack gap="lg">
      {/* Dynamic threshold explanation */}
      <Alert
        variant="light"
        color="blue"
        icon={<Info size={16} />}
        title="Dynamic Escalation Model"
      >
        <Text fz={12}>
          Deferral threshold is automatically set to the halfway point of the cohort
          (e.g., 4th absence for an 8-session cohort, 3rd for a 6-session cohort).
          Rules are matched by escalation tier, not by specific session count or absence number.
        </Text>
      </Alert>

      {/* Coverage Indicator */}
      {coverage && (
        <Alert
          variant="light"
          color={coverage.all_covered ? 'green' : 'yellow'}
          icon={coverage.all_covered ? <Check size={16} /> : <AlertTriangle size={16} />}
          title={
            coverage.all_covered
              ? 'All escalation tiers covered'
              : 'Warning: gaps in tier coverage'
          }
        >
          {!coverage.all_covered &&
            coverage.tiers
              .filter((t) => !t.covered)
              .map((t) => (
                <Stack key={t.escalation_tier} gap={2} mt={4}>
                  <Text fz={12} fw={600}>
                    {tierLabel(t.escalation_tier)}:
                  </Text>
                  {t.gaps.map((gap, i) => (
                    <Text key={i} fz={12} c="dimmed">
                      • {gap.description}
                    </Text>
                  ))}
                </Stack>
              ))}
        </Alert>
      )}

      {/* Tier-based rule sections */}
      {tierGroups.map((group, idx) => (
        <div key={group.tier}>
          {idx > 0 && <Divider mb="lg" />}
          <TierRuleSection
            tier={group.tier}
            label={group.label}
            rules={group.rules}
            onAdd={() => handleOpenCreate(group.tier)}
            onEdit={handleOpenEdit}
            onDelete={handleOpenDelete}
          />
        </div>
      ))}

      {/* Create/Edit Rule Modal */}
      <Modal
        opened={modalOpened}
        onClose={closeModal}
        title={editingRule ? 'Edit Rule' : 'Add Rule'}
        size="md"
      >
        <Stack gap="md">
          <Select
            label="Escalation Tier"
            data={ESCALATION_TIER_OPTIONS}
            value={formData.escalation_tier}
            onChange={(val) =>
              setFormData({ ...formData, escalation_tier: (val as EscalationTier) || 'first_warning' })
            }
          />

          <Select
            label="Absence Type"
            data={ABSENCE_TYPE_OPTIONS}
            value={formData.absence_type}
            onChange={(val) =>
              setFormData({ ...formData, absence_type: (val as AbsenceRuleType) || 'unexcused' })
            }
          />

          <Switch
            label="Is First Session"
            description="Only applies to first_warning and first_warning_with_offer tiers"
            checked={formData.is_first_session}
            onChange={(e) =>
              setFormData({ ...formData, is_first_session: e.currentTarget.checked })
            }
          />

          <Switch
            label="No Email"
            description="Skip sending email for this scenario"
            checked={formData.no_email}
            onChange={(e) => {
              const noEmail = e.currentTarget.checked;
              setFormData({
                ...formData,
                no_email: noEmail,
                template_id: noEmail ? null : formData.template_id,
              });
            }}
          />

          {!formData.no_email && (
            <Select
              label="Email Template"
              placeholder="Select a template"
              data={templateOptions}
              value={formData.template_id}
              onChange={(val) => setFormData({ ...formData, template_id: val })}
              searchable
            />
          )}

          <NumberInput
            label="Priority"
            description="Lower numbers are evaluated first"
            min={0}
            value={formData.priority}
            onChange={(val) => setFormData({ ...formData, priority: Number(val) || 0 })}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={closeModal}>
              Cancel
            </Button>
            <Button color="medleyGreen" onClick={handleSave} loading={saving}>
              {editingRule ? 'Save Changes' : 'Add Rule'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        title="Delete Rule"
        size="sm"
      >
        <Stack gap="md">
          <Text fz="sm">
            Are you sure you want to delete this rule? This may create coverage gaps.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={closeDeleteModal}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDelete}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

function TierRuleSection({
  tier,
  label,
  rules,
  onAdd,
  onEdit,
  onDelete,
}: {
  tier: EscalationTier;
  label: string;
  rules: AbsenceEmailRuleWithTemplateName[];
  onAdd: () => void;
  onEdit: (rule: AbsenceEmailRuleWithTemplateName) => void;
  onDelete: (rule: AbsenceEmailRuleWithTemplateName) => void;
}) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Group gap="sm">
          <Title order={4} fw={700} fz={16}>
            {label}
          </Title>
          <Badge size="xs" variant="light" color={TIER_COLORS[tier] ?? 'gray'} radius={3}>
            {rules.length} rule{rules.length !== 1 ? 's' : ''}
          </Badge>
        </Group>
        <Button
          leftSection={<Plus size={14} />}
          size="xs"
          variant="light"
          color="medleyGreen"
          onClick={onAdd}
        >
          Add Rule
        </Button>
      </Group>

      <Paper withBorder radius="md" styles={{ root: { boxShadow: 'none' } }}>
        {/* Column headers */}
        <Group
          gap="xl"
          px="lg"
          py="sm"
          style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}
        >
          <Text size="xs" fw={600} tt="uppercase" c="dimmed" style={{ minWidth: 70, letterSpacing: '0.5px' }}>
            1st Sess?
          </Text>
          <Text size="xs" fw={600} tt="uppercase" c="dimmed" style={{ minWidth: 80, letterSpacing: '0.5px' }}>
            Type
          </Text>
          <Text size="xs" fw={600} tt="uppercase" c="dimmed" style={{ flex: 1, letterSpacing: '0.5px' }}>
            Template
          </Text>
          <Text size="xs" fw={600} tt="uppercase" c="dimmed" style={{ minWidth: 70, letterSpacing: '0.5px' }}>
            No Email?
          </Text>
          <Box style={{ minWidth: 60 }}>
            <Text size="xs" fw={600} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.5px' }}>
              Actions
            </Text>
          </Box>
        </Group>
        <Divider />

        {/* Rows */}
        <Stack gap={0}>
          {rules.length === 0 ? (
            <Center py="lg">
              <Text c="dimmed" size="sm">
                No rules for {label} tier.
              </Text>
            </Center>
          ) : (
            rules.map((rule) => {
              const isHovered = hoveredId === rule.id;
              return (
                <Box
                  key={rule.id}
                  style={{
                    borderLeft: isHovered ? `3px solid ${TIER_COLORS[tier] === 'blue' ? '#1971C2' : '#28463A'}` : '3px solid transparent',
                    backgroundColor: isHovered ? '#F5F7F6' : 'transparent',
                    cursor: 'default',
                    transition: 'background-color 0.15s ease',
                  }}
                  onMouseEnter={() => setHoveredId(rule.id)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  <Group
                    gap="xl"
                    py="sm"
                    px="lg"
                    style={{
                      paddingLeft: isHovered ? 'calc(var(--mantine-spacing-lg) - 3px)' : 'var(--mantine-spacing-lg)',
                    }}
                  >
                    {/* 1st Session */}
                    <Text size="xs" c="dimmed" style={{ minWidth: 70 }}>
                      {rule.is_first_session ? 'Yes' : '—'}
                    </Text>

                    {/* Type */}
                    <Box style={{ minWidth: 80 }}>
                      <Badge
                        size="xs"
                        variant="light"
                        color={
                          rule.absence_type === 'unexcused'
                            ? 'red'
                            : rule.absence_type === 'business'
                              ? 'blue'
                              : rule.absence_type === 'personal'
                                ? 'orange'
                                : 'gray'
                        }
                        radius={3}
                        tt="capitalize"
                      >
                        {rule.absence_type}
                      </Badge>
                    </Box>

                    {/* Template */}
                    <Text size="xs" style={{ flex: 1 }}>
                      {rule.no_email ? (
                        <Text span c="dimmed">
                          — (no email)
                        </Text>
                      ) : (
                        rule.template_name || (
                          <Text span c="red">
                            Missing template
                          </Text>
                        )
                      )}
                    </Text>

                    {/* No Email */}
                    <Box style={{ minWidth: 70 }}>
                      {rule.no_email && (
                        <Badge size="xs" variant="light" color="gray" radius={3}>
                          ✓
                        </Badge>
                      )}
                    </Box>

                    {/* Actions */}
                    <Group gap={4} style={{ minWidth: 60 }}>
                      <ActionIcon variant="subtle" size="sm" onClick={() => onEdit(rule)}>
                        <Pencil size={14} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        color="red"
                        onClick={() => onDelete(rule)}
                      >
                        <Trash2 size={14} />
                      </ActionIcon>
                    </Group>
                  </Group>
                </Box>
              );
            })
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
